(() => {
  const SECTION_SELECTOR = '[data-parallax-content-columns]';
  const COLUMN_SELECTOR = '[data-content-column]';
  const GRID_SELECTOR = '[data-parallax-content-columns-grid]';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 768px)');
  const MOBILE_SCALE_MIN = 0.7;
  const SCALE_TRIGGER_GAP = 50;
  const ACTIVE_CLASS = 'is-active';
  const PERSPECTIVE = 900;
  const MAX_ROTATE = 8;
  const MAX_SKEW = 3;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const initOpacityReveal = (columns) => {
    if (window.ScrollTrigger) return;

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(ACTIVE_CLASS);
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 })
      : null;

    if (observer) {
      columns.forEach((column) => observer.observe(column));
    } else if (typeof observeInViewBatch === 'function') {
      observeInViewBatch(
        {
          each(callback) {
            columns.forEach((col, index) => callback(index, col));
          },
        },
        (el) => el.classList.add(ACTIVE_CLASS),
      );
    }
  };

  const initParallax = (section, columns) => {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger || (gsap && gsap.ScrollTrigger);

    if (!gsap || !ScrollTrigger || !DESKTOP_QUERY.matches) return [];

    gsap.registerPlugin(ScrollTrigger);

    const triggers = [];
    const offsetIncrement = 40;
    const baseOffset = 20;

    columns.forEach((column, index) => {
      const offset = baseOffset + offsetIncrement * index;
      let opacityTriggered = false;

      const ensureOpacity = () => {
        if (opacityTriggered) return;
        if (column.classList.contains(ACTIVE_CLASS)) {
          opacityTriggered = true;
          return;
        }

        const rect = column.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;

        if (rect.top <= viewportCenter) {
          column.classList.add(ACTIVE_CLASS);
          opacityTriggered = true;
        }
      };

      const tween = gsap.fromTo(
        column,
        { y: `${offset}vh` },
        {
          y: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6 + index * 0.1,
            invalidateOnRefresh: true,
            onUpdate: ensureOpacity,
            onRefresh: ensureOpacity,
          },
        },
      );

      triggers.push(tween.scrollTrigger);
    });

    return triggers;
  };

  const destroyParallax = (columns, triggers) => {
    const gsap = window.gsap;

    if (Array.isArray(triggers)) {
      triggers.forEach((trigger) => {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      });
    }

    if (gsap) {
      columns.forEach((column) => gsap.set(column, { clearProps: 'transform' }));
    } else {
      columns.forEach((column) => {
        column.style.removeProperty('transform');
      });
    }
  };

  const createMobileStackController = (columns) => {
    if (!columns.length) return null;

    const controller = {
      frameId: null,
      columns,
    };

    const animate = () => {
      controller.frameId = null;

      controller.columns.forEach((column, index) => {
        const nextColumn = controller.columns[index + 1];
        if (!nextColumn) {
          column.style.transform = 'scale(1)';
          column.style.opacity = '1';
          return;
        }

        const columnRect = column.getBoundingClientRect();
        const nextRect = nextColumn.getBoundingClientRect();
        const distance = nextRect.top - columnRect.bottom;
        const progress = clamp((SCALE_TRIGGER_GAP - distance) / SCALE_TRIGGER_GAP, 0, 1);

        const scale = 1 - progress * (1 - MOBILE_SCALE_MIN);
        const opacity = 1 - progress;
        const rotate = -progress * MAX_ROTATE;
        const skew = progress * MAX_SKEW;

        column.style.transform = `perspective(${PERSPECTIVE}px) rotateX(${rotate}deg) skewY(${skew}deg) scale(${scale})`;
        column.style.opacity = `${opacity}`;
      });
    };

    const scheduleAnimation = () => {
      if (controller.frameId === null) {
        controller.frameId = requestAnimationFrame(animate);
      }
    };

    const handleScroll = () => scheduleAnimation();
    const handleResize = () => scheduleAnimation();

    columns.forEach((column, index) => {
      column.style.zIndex = columns.length - index;
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    scheduleAnimation();

    return {
      destroy() {
        if (controller.frameId !== null) {
          cancelAnimationFrame(controller.frameId);
          controller.frameId = null;
        }

        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);

        controller.columns.forEach((column) => {
          column.style.removeProperty('transform');
          column.style.removeProperty('opacity');
          column.style.removeProperty('z-index');
        });
      },
    };
  };

  const initSection = (section) => {
    if (section.dataset.parallaxContentColumnsInitialized === 'true') return;

    const grid = section.querySelector(GRID_SELECTOR);
    const columns = Array.from(section.querySelectorAll(COLUMN_SELECTOR));
    if (!grid || !columns.length) return;

    section.dataset.parallaxContentColumnsInitialized = 'true';

    initOpacityReveal(columns);

    let parallaxTriggers = [];
    let mobileStackController = null;

    const handleViewportChange = () => {
      destroyParallax(columns, parallaxTriggers);
      parallaxTriggers = [];

      if (DESKTOP_QUERY.matches) {
        if (mobileStackController) {
          mobileStackController.destroy();
          mobileStackController = null;
        }

        parallaxTriggers = initParallax(section, columns);
        return;
      }

      if (!mobileStackController) {
        mobileStackController = createMobileStackController(columns);
      }
    };

    if (typeof window.onWidthResize === 'function') {
      window.onWidthResize(handleViewportChange);
    } else {
      window.addEventListener('resize', handleViewportChange);
    }

    handleViewportChange();
  };

  const initAllSections = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  document.addEventListener('DOMContentLoaded', initAllSections);
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target || document;
    container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  });
})();
